import React from 'react';
import { useNotificationStore } from '../stores/notification.store';

export const Notifications: React.FC = () => {
  const { notifications, removeNotification } = useNotificationStore();

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  const getStyles = (type: string) => {
    switch (type) {
      case 'success':
        return {
          border: 'border-green-200',
          bg: 'bg-green-50',
          icon: 'text-green-600 bg-green-100',
          title: 'text-green-800',
          text: 'text-green-700',
        };
      case 'error':
        return {
          border: 'border-red-200',
          bg: 'bg-red-50',
          icon: 'text-red-600 bg-red-100',
          title: 'text-red-800',
          text: 'text-red-700',
        };
      case 'warning':
        return {
          border: 'border-yellow-200',
          bg: 'bg-yellow-50',
          icon: 'text-yellow-600 bg-yellow-100',
          title: 'text-yellow-800',
          text: 'text-yellow-700',
        };
      case 'info':
      default:
        return {
          border: 'border-blue-200',
          bg: 'bg-blue-50',
          icon: 'text-blue-600 bg-blue-100',
          title: 'text-blue-800',
          text: 'text-blue-700',
        };
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 max-w-sm">
      {notifications.map((notification) => {
        const styles = getStyles(notification.type);
        return (
          <div
            key={notification.id}
            className={`${styles.bg} border ${styles.border} rounded-lg shadow-lg p-4 animate-slide-in backdrop-blur-sm`}
          >
            <div className="flex items-start">
              {/* Icon */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full ${styles.icon} flex items-center justify-center font-bold text-lg`}>
                {getIcon(notification.type)}
              </div>

              {/* Content */}
              <div className="ml-3 flex-1">
                <h3 className={`text-sm font-semibold ${styles.title}`}>
                  {notification.title}
                </h3>
                {notification.message && (
                  <p className={`mt-1 text-xs ${styles.text} leading-relaxed`}>
                    {notification.message}
                  </p>
                )}
              </div>

              {/* Close Button */}
              <button
                onClick={() => removeNotification(notification.id)}
                className="ml-3 flex-shrink-0 inline-flex text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                aria-label="Close notification"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            {/* Progress bar for timed notifications */}
            {notification.duration && (
              <div className="mt-3 h-1 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    notification.type === 'success' ? 'bg-green-600' :
                    notification.type === 'error' ? 'bg-red-600' :
                    notification.type === 'warning' ? 'bg-yellow-600' :
                    'bg-blue-600'
                  } animate-shrink`}
                  style={{
                    animationDuration: `${notification.duration}ms`,
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
